import dayjs from 'dayjs';

import RecruitApiClient from '../../RecruitApiClient';

import type {
  IAddTaskParams,
  IAlayaCareExportParams,
  IApplication,
  IApplicationTask,
  IAttachment,
  ICognologyData,
  ICognologyExportParams,
  IEmailTemplate,
  IEmailTemplateList,
  IFit2WorkData,
  IFit2WorkExportParams,
  IFormResponseListItem,
  IFormsOption,
  IFoundUExportParams,
  IGetEventsParams,
  IGetSmartFormsParams,
  IHronboardExportParams,
  IHronboardResponse,
  IIntegrationsResponse,
  IIntelliHrData,
  IInviteByEmailParams,
  IPreviewData,
  IReferooData,
  IReferooExportParams,
  IRevolveExportParams,
  IRoublerExportParams,
  ISendExternalEmailParams,
  ISparkHireData,
  ISparkHireExportParams,
  ITimestamp,
  IVideoInterview,
  IXRefExportParams
} from './types';
import type { IEvent, IEventType, IRecruiter } from '../../ScoutCalendar/types';
import type { IApprovalTemplate } from '../ApprovalForms/ApprovalTemplates/types';
import type { IAnswer, IApprovalForm } from '../ApprovalForms/types';
import type { IActivity } from '../Candidate/types';
import type { IRecipientUser } from '../Components/CustomUIElements/RecipientInput';
import type { ICountries } from '../Components/sharedTypes';
import type { IApplicantQuestion } from '../Components/Utilities/QuestionFieldGenerator';
import type { IJob } from '../EditJob/types';

class Api {
  getApplicationData = async (
    jobId: number,
    applicationId: number,
    index?: string
  ): Promise<{ res: IApplication }> => {
    const indexQuery = index === undefined ? '' : `?i=${index}`;

    return RecruitApiClient.get(
      `/api/v4/jobs/${jobId}/applications/${applicationId}${indexQuery}`,
      null
    );
  };

  getApplicationStatuses = async (jobId: number) =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/job_application_statuses`, null);

  updateApplicationStatus = async (jobId: number, applicationId: number, statusId: number) =>
    RecruitApiClient.put(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/application_status/${statusId}`
    );

  updateCandidateLinkedInURL = async (candidateId: number, linkedInURL: string) =>
    RecruitApiClient.put(`/api/v4/candidates/${candidateId}`, {
      candidate: { linkedin_url: linkedInURL }
    });

  getJobsOptions = async (jobId: number) =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/bulk_move_applications`, null);

  getCsvExportTemplates = async () => RecruitApiClient.get(`/api/v4/csv_export_templates`, null);

  getCandidateDataWithCsvTemplate = async (
    jobId: number,
    applicationIds: number[],
    templateId: number
  ) =>
    RecruitApiClient.get(
      `/api/v4/jobs/${jobId}/applications/export_to_csv?template_id=${templateId}&application_ids=${applicationIds.join(',')}`,
      null
    );

  moveApplication = async (
    jobId: number,
    param: { target_job: string; application_ids: number[] }
  ) => RecruitApiClient.post(`/api/v4/jobs/${jobId}/bulk_move_applications`, param);

  trashApplication = async (jobId: number, applicationId: number) =>
    RecruitApiClient.delete(`/api/v4/jobs/${jobId}/applications`, {
      application_ids: [applicationId]
    });

  updateApplicationNotepad = async (jobId: number, applicationId: number, note: string) =>
    RecruitApiClient.put(`/api/v4/jobs/${jobId}/applications/${applicationId}/update_notepad`, {
      suitability_comment: note
    });

  sendApplicationSummary = async (jobId: number, applicationId: number, emails: string[]) =>
    RecruitApiClient.post(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/send_individual_summary`,
      { emails }
    );

  updateSummary = async (jobId: number, applicationId: number) =>
    RecruitApiClient.post(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/application_summary`
    );

  getHrQuestions = async (jobId: number, applicationId: number) =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/hr_answers`);

  saveHrQuestion = async (jobId: number, applicationId: number, questionId: number, params: any) =>
    RecruitApiClient.put(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/hr_answers/${questionId}`,
      { hr_answer_set: params }
    );

  getRatingSetForApplication = async (jobId: number, applicationId: number) =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/rating_set`);

  resetRatingForApplication = async (jobId: number, applicationId: number) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/reset_rating`);

  rateResponses = async (jobId: number, applicationId: number, params: any) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/rating_sets`, {
      rating_set: params
    });

  cloneApplication = async (
    jobId: number,
    application_id: number,
    param: { to_job_id: string; cloning_assets: boolean }
  ) =>
    RecruitApiClient.post(
      `/api/v4/jobs/${jobId}/applications/${application_id}/clone_application`,
      param
    );

  resendApplication = async (jobId: number, applicationId: number) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/resend_application`);

  deleteApplication = async (jobId: number, applicationId: number) =>
    RecruitApiClient.delete(`/api/v4/jobs/${jobId}/applications/${applicationId}`);

  getSearchIndex = async (jobId: number, applicationId: number) =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/search_index`, null);

  updateSearchIndex = async (jobId: number, applicationId: number) =>
    RecruitApiClient.put(`/api/v4/jobs/${jobId}/applications/${applicationId}/search_index`, {});

  deleteSearchIndex = async (jobId: number, applicationId: number) =>
    RecruitApiClient.delete(`/api/v4/jobs/${jobId}/applications/${applicationId}/search_index`);

  updateApplicationRating = async (
    jobId: number,
    applicationId: number,
    param: { ref_check_rating: string | null; interview_rating: string | null }
  ) => RecruitApiClient.put(`/api/v4/jobs/${jobId}/applications/${applicationId}`, param);

  updateApplicationSource = async (jobId: number, applicationId: number, sourceId: number) =>
    RecruitApiClient.put(`/api/v4/jobs/${jobId}/applications/${applicationId}/update_source`, {
      application: { source_id: sourceId }
    });

  updateUiPreferences = async (param: { sorting: { order: string[] }; source: string }) =>
    RecruitApiClient.post(`/api/v4/ui_preferences`, { ui_preference: param });

  getUiPreferences = async () =>
    RecruitApiClient.get(`/api/v4/ui_preferences?source=application`, null);

  getParsedExperience = async (jobId: number, applicationId: number) =>
    RecruitApiClient.get(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/parsed_experience`,
      null
    );

  // eslint-disable-next-line class-methods-use-this
  flagIncorrectExperience = async (jobId: number, applicationId: number, notes: string) =>
    RecruitApiClient.post(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/parsed_experience/flag_incorrect`,
      { notes }
    );

  // eslint-disable-next-line class-methods-use-this
  parseApplicationResume = async (jobId: number, applicationId: number) =>
    RecruitApiClient.post(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/parsed_experience/parse_resume`
    );

  addRecord = async (
    jobId: number,
    applicationId: number,
    params: { subject: string; body: string }
  ) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/permanent_record`, {
      note: params
    });

  getEvents = async (params: IGetEventsParams) =>
    RecruitApiClient.get('/api/v4/calendar/events', params);

  getSmsTemplates = async () => RecruitApiClient.get('/api/v4/sms_templates', {});

  getSmsTemplatePlaceholders = async (kind?: string) =>
    RecruitApiClient.get('/api/v4/sms_templates/placeholders', { kind });

  saveSmsTemplate = async (smsTemplateName: string, smsMessage: string) =>
    RecruitApiClient.post(`/api/v4/sms_templates`, {
      sms_template: { title: smsTemplateName, body: smsMessage }
    });

  getSubEntityGroupedEmailTemplates = async (jobId: number): Promise<{ res: IEmailTemplateList }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/email_templates`, { group_templates: true });

  getEmailLetterheadGroupedEmailTemplates = async (
    type?: string
  ): Promise<{ res: IEmailTemplateList }> =>
    RecruitApiClient.get(`/api/v4/calendar/email_templates/grouped_email_templates`, { type });

  // eslint-disable-next-line class-methods-use-this
  getEmailTemplatePlaceholders = async (
    type?: string
  ): Promise<{ res: { placeholders: string } }> =>
    RecruitApiClient.get(`/api/v4/calendar/email_templates/placeholders`, { type: type ?? '' });

  getEmailTemplateFromCalendar = async (templateId: number): Promise<{ res: IEmailTemplate }> =>
    RecruitApiClient.get(`/api/v4/calendar/email_templates/${templateId}`, {});

  getEmailTemplateFromJob = async (
    templateId: number,
    jobId: number
  ): Promise<{ res: IEmailTemplate }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/email_templates/${templateId}`, {});

  addToEventSms = async (
    jobId: number,
    eventId: number,
    applicationIds: number[],
    content: string,
    smsTemplateId?: number
  ) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/bulk_sms/add_to_event`, {
      event_id: eventId,
      applications_ids: applicationIds,
      content,
      sms_template_id: smsTemplateId,
      added: true
    });

  sendInviteSms = async (
    jobId: number,
    eventTypeId: number,
    applicationIds: number[],
    content: string,
    smsTemplateId?: number
  ) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/bulk_sms/invite_to_timeslot`, {
      event_type_id: eventTypeId,
      applications_ids: applicationIds,
      content,
      sms_template_id: smsTemplateId,
      invited: true
    });

  sendEmail = async (
    jobId: number,
    eventId: number,
    emailTemplateId: number,
    content: string,
    applicationIds: number[],
    subject: string
  ) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/email`, {
      email: {
        event_id: eventId,
        email_template_id: emailTemplateId,
        subject,
        content,
        application_ids: applicationIds
      }
    });

  getApplicationActivities = async (
    jobId: number,
    applicationId: number
  ): Promise<{ res: { activities: IActivity[] } }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/activities`, {
      override_limit: true,
      limit: 100
    });

  getApprovalTemplates = async (): Promise<{
    res: { enabled_requisition_forms: IApprovalTemplate[] };
  }> => RecruitApiClient.get(`/api/v4/requisition_forms/enabled_for_applications`);

  sendExternalEmails = async (jobId: number, params: ISendExternalEmailParams) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/bulk_external_email`, params);

  getSmartForms = async (
    params: IGetSmartFormsParams
  ): Promise<{ res: { smart_forms: IFormsOption[] } }> =>
    RecruitApiClient.get(`/api/v4/smart_forms`, params);

  getFormQuestions = async (params: {
    form_id: number;
    form_response_id?: number;
    enabled_only?: boolean;
  }): Promise<{ res: { fields: IApplicantQuestion[] } }> =>
    RecruitApiClient.get(`/api/v4/fields`, params);

  addForm = async (jobId: number, applicationId: number, formId: number, params: any) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/form_responses`, {
      form_id: formId,
      form_response: { answers_attributes: params }
    });

  sms = async (
    jobId: number,
    applicationId: number,
    params: { to: string; content: string; sms_template_id?: number }
  ) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/sms`, {
      sms: params
    });

  sendForm = async (jobId: number, applicationId: number, params: any) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/send_form`, {
      send_form: params
    });

  sendFormBySMS = async (jobId: number, applicationId: number, params: any) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/send_sms_form`, {
      send_sms_form: params
    });

  getCareAdvantageEvents = async (
    jobId: number
  ): Promise<{
    res: { care_advantage_events: IEventType[]; has_care_advantage_integration: boolean };
  }> => RecruitApiClient.get(`/api/v4/jobs/${jobId}/auto_emails/care_advantage_events`);

  getApplicationAttachments = async (
    jobId: number,
    applicationId: number
  ): Promise<{ res: { attachments: IAttachment[] } }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/attachments`, null);

  getAttachmentTitleOptions = async (
    jobId: number,
    applicationId: number
  ): Promise<{ res: { file_names: string[] } }> =>
    RecruitApiClient.get(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/attachments/file_name_selection`,
      null
    );

  // eslint-disable-next-line class-methods-use-this
  updateAttachmentTitle = async (
    jobId: number,
    applicationId: number,
    attachmentId: number,
    attachmentTitle: string
  ) =>
    RecruitApiClient.put(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/attachments/${attachmentId}/update_attachment_title`,
      { attachment_title: attachmentTitle }
    );

  deleteAttachment = async (jobId: number, applicationId: number, attachmentId: number) =>
    RecruitApiClient.delete(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/attachments/${attachmentId}`
    );

  createRecordingConversion = async (
    applicationId: number,
    assetId: number
  ): Promise<{ res: { id: number; status: 'pending' } }> =>
    RecruitApiClient.post(`/api/v4/applications/${applicationId}/recording_conversions`, {
      asset_id: assetId
    });

  getRecruiters = async (): Promise<{ res: { users: IRecruiter[] } }> =>
    RecruitApiClient.get('/api/v4/calendar/users', {});

  // eslint-disable-next-line class-methods-use-this
  getUsers = async (): Promise<{ res: { users: IRecipientUser[] } }> =>
    RecruitApiClient.get('/api/v4/users', {});

  getEventJobs = async (): Promise<{ res: { jobs: IJob[] } }> =>
    RecruitApiClient.get('/api/v4/calendar/jobs', {});

  getEventTypes = async (): Promise<{ res: { event_types: IEventType[] } }> =>
    RecruitApiClient.get('/api/v4/calendar/event_types', {});

  inviteByEmail = async (params: IInviteByEmailParams) =>
    RecruitApiClient.post(`/api/v4/calendar/invitations`, params);

  // eslint-disable-next-line class-methods-use-this
  sendCronofyInvitation = async (params: {
    user_ids: number[];
    event_type_id: number;
    job_id: number;
    slots: { starts_at: string; ends_at: string }[];
    book_now?: boolean;
    event_title?: string;
    event_description?: string;
    location_type?: number;
    location_value?: string;
    enable_video_conferencing?: boolean;
    invitation: {
      application_ids: number[];
      subject: string;
      body: string;
      email_template_id?: string;
    };
  }) => RecruitApiClient.post(`/api/v4/calendar/cronofy_invitations`, params);

  // eslint-disable-next-line class-methods-use-this
  getConferencingStatus = async (): Promise<{ res: { zoom_linked: boolean } }> =>
    RecruitApiClient.get(`/api/v4/cronofy/conferencing_status`, null);

  previewAttachment = async (
    jobId: number,
    applicationId: number,
    attachmentId: number,
    index: number
  ): Promise<{ res: IPreviewData }> =>
    RecruitApiClient.get(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/attachments/${attachmentId}`,
      { index }
    );

  getVideoTimestamps = async (
    applicationId: number,
    assetId: number
  ): Promise<{ res: { timestamps: ITimestamp[] } }> =>
    RecruitApiClient.get(`/api/v4/applications/${applicationId}/video_timestamps`, {
      asset_id: assetId
    });

  createVideoTimestamp = async (
    applicationId: number,
    assetId: number,
    timestampName: string,
    timestampValue: string
  ) =>
    RecruitApiClient.post(`/api/v4/applications/${applicationId}/video_timestamps`, {
      asset_id: assetId,
      timestamp: { name: timestampName, value: timestampValue }
    });

  updateVideoTimestamp = async (
    applicationId: number,
    assetId: number,
    timestampId: number,
    timestampName: string,
    timestampValue: string
  ) =>
    RecruitApiClient.put(`/api/v4/applications/${applicationId}/video_timestamps/${timestampId}`, {
      asset_id: assetId,
      timestamp: { name: timestampName, value: timestampValue }
    });

  deleteVideoTimestamp = async (applicationId: number, assetId: number, timestampId: number) =>
    RecruitApiClient.delete(`/api/v4/applications/${applicationId}/video_timestamps/${timestampId}`, {
      asset_id: assetId
    });

  getApplicationTasks = async (
    jobId: number,
    applicationId: number
  ): Promise<{ res: { tasks: IApplicationTask[] } }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/tasks`);

  updateApplicationTask = async (
    jobId: number,
    applicationId: number,
    taskId: number,
    params: any
  ) =>
    RecruitApiClient.put(`/api/v4/jobs/${jobId}/applications/${applicationId}/tasks/${taskId}`, {
      task: params
    });

  getWorkflows = async (applicationId: number) =>
    RecruitApiClient.get(`/api/v4/workflows?application_id=${applicationId}`);

  getAssignableUsers = async () => RecruitApiClient.get(`/api/v4/tasks/assignable_users`);

  assignWorkflow = async (applicationId: number, params: { id: number; user_id: number }) =>
    RecruitApiClient.post(`/api/v4/workflows?application_id=${applicationId}`, params);

  addTask = async (jobId: number, applicationId: number, params: IAddTaskParams) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/tasks`, {
      task: params
    });

  deleteTask = async (jobId: number, applicationId: number, taskId: number) =>
    RecruitApiClient.delete(`/api/v4/jobs/${jobId}/applications/${applicationId}/tasks/${taskId}`);

  getApplicationEvents = async (
    jobId: number,
    applicationId: number
  ): Promise<{ res: { events: IEvent[] } }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/events`);

  getFormResponses = async (
    jobId: number,
    applicationId: number
  ): Promise<{ res: { form_responses: IFormResponseListItem[] } }> =>
    RecruitApiClient.get(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/form_responses`,
      null
    );

  showFormResponse = async (
    jobId: number,
    applicationId: number,
    formResponseId: number
  ): Promise<{ res: { question_and_answers: IAnswer[] } }> =>
    RecruitApiClient.get(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/form_responses/${formResponseId}`
    );

  editFormResponse = async (
    jobId: number,
    applicationId: number,
    formResponseId: number,
    params: any
  ) =>
    RecruitApiClient.put(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/form_responses/${formResponseId}`,
      {
        form_response: { answers_attributes: params }
      }
    );

  deleteFormResponse = async (jobId: number, applicationId: number, formResponseId: number) =>
    RecruitApiClient.delete(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/form_responses/${formResponseId}`
    );

  getVideoInterview = async (ApplicationId: number): Promise<{ res: IVideoInterview }> =>
    RecruitApiClient.get(`/api/v4/video_interviews/${ApplicationId}`);

  deleteVideoInterview = async (ApplicationId: number) =>
    RecruitApiClient.delete(`/api/v4/video_interviews/${ApplicationId}`);

  getVideoInterviewBookmarks = async (
    ApplicationId: number
  ): Promise<{ res: { bookmarks: ITimestamp[] } }> =>
    RecruitApiClient.get(`/api/v4/video_interviews/${ApplicationId}/bookmarks`);

  deleteVideoInterviewBookmark = async (ApplicationId: number, id: string) =>
    RecruitApiClient.delete(`/api/v4/video_interviews/${ApplicationId}/bookmarks/${id}`);

  createVideoInterviewBookmark = async (ApplicationId: number, name: string, value: number) =>
    RecruitApiClient.post(`/api/v4/video_interviews/${ApplicationId}/bookmarks`, {
      name,
      value
    });

  updateVideoInterviewBookmark = async (
    ApplicationId: number,
    id: string,
    name: string,
    value: number
  ) =>
    RecruitApiClient.put(`/api/v4/video_interviews/${ApplicationId}/bookmarks/${id}`, {
      name,
      value
    });

  exportToOnboard = async (jobId: number, applicationId: number, startDate: Date) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/scout_onboard_exports`, {
      start_date: dayjs(startDate).format('YYYY-MM-DD'),
      application_ids: [applicationId]
    });

  exportToNewOnboard = async (jobId: number, applicationId: number) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/new_onboard_exports`, {
      application_ids: [applicationId]
    });

  getRevolveData = async (applicationId: number) =>
    RecruitApiClient.get(`/api/v4/integrations/revolve_exports`, { application_id: applicationId });

  revolveExport = async (applicationId: number, params: IRevolveExportParams) =>
    RecruitApiClient.post(`/api/v4/integrations/revolve_exports`, {
      application_id: applicationId,
      revolve_record: params
    });

  getXrefPackages = async (applicationId: number) =>
    RecruitApiClient.get(`/api/v4/integrations/xref/packages`, { application_id: applicationId });

  xRefExport = async (applicationId: number, params: IXRefExportParams) =>
    RecruitApiClient.post(`/api/v4/integrations/xref`, {
      application_id: applicationId,
      ...params
    });

  getIntegrationsData = async (applicationId: number): Promise<{ res: IIntegrationsResponse }> =>
    RecruitApiClient.get(`/api/v4/integrations`, { application_id: applicationId });

  resolveUnusualActivity = async (
    applicationId: number,
    params: { referee_id: number; note: string; reference_id: number; resolution?: number }
  ) =>
    RecruitApiClient.post(`/api/v4/integrations/xref/resolve_unusual_activity`, {
      application_id: applicationId,
      ...params
    });

  getCountriesStates = async (): Promise<{ res: { countries: ICountries[] } }> =>
    RecruitApiClient.get('/api/v4/countries');

  getMvsData = async (applicationId: number) =>
    RecruitApiClient.get(`/api/v4/integrations/mvs`, { application_id: applicationId });

  mvsExport = async (
    applicationId: number,
    params: { package_id: string; blueprint_ids: string[] }
  ) =>
    RecruitApiClient.post(`/api/v4/integrations/mvs`, {
      application_id: applicationId,
      ...params
    });

  getReferooData = async (applicationId: number): Promise<{ res: IReferooData }> =>
    RecruitApiClient.get(`/api/v4/integrations/referoo`, { application_id: applicationId });

  referooExport = async (applicationId: number, params: IReferooExportParams) =>
    RecruitApiClient.post(`/api/v4/integrations/referoo`, {
      application_id: applicationId,
      ...params
    });

  foundUExport = async (applicationId: number, params: IFoundUExportParams) =>
    RecruitApiClient.post(`/api/v4/integrations/foundu`, {
      application_id: applicationId,
      ...params
    });

  getTestGridPackages = async (applicationId: number): Promise<{ res: Record<string, string> }> =>
    RecruitApiClient.get(`/api/v4/integrations/test_grids/packages`, {
      application_id: applicationId
    });

  testGridExport = async (applicationId: number, packageId: string) =>
    RecruitApiClient.post(`/api/v4/integrations/test_grids`, {
      application_id: applicationId,
      package_id: packageId
    });

  getHronboardData = async (applicationId: number): Promise<{ res: IHronboardResponse }> =>
    RecruitApiClient.get(`/api/v4/integrations/hro_exports`, { application_id: applicationId });

  hronboardExport = async (
    applicationId: number,
    params: IHronboardExportParams
  ): Promise<{ res: { message: string } }> =>
    RecruitApiClient.post(`/api/v4/integrations/hro_exports`, {
      application_id: applicationId,
      hro_record: params
    });

  roublerExport = async (applicationId: number, params: IRoublerExportParams) =>
    RecruitApiClient.post(`/api/v4/integrations/roubler_exports`, {
      application_id: applicationId,
      roubler_record: params
    });

  getFit2WorkData = async (applicationId: number): Promise<{ res: IFit2WorkData }> =>
    RecruitApiClient.get(`/api/v4/integrations/fit2work`, { application_id: applicationId });

  fit2WorkExport = async (
    applicationId: number,
    params: IFit2WorkExportParams
  ): Promise<{ res: { success: string } }> =>
    RecruitApiClient.post(`/api/v4/integrations/fit2work`, {
      application_id: applicationId,
      ...params
    });

  getSparkHireData = async (applicationId: number): Promise<{ res: ISparkHireData }> =>
    RecruitApiClient.get(`/api/v4/integrations/sparkhire_exports`, {
      application_id: applicationId
    });

  sparkHireExport = async (
    applicationId: number,
    params: ISparkHireExportParams
  ): Promise<{ res: { success: string } }> =>
    RecruitApiClient.post(`/api/v4/integrations/sparkhire_exports`, {
      application_id: applicationId,
      sparkhire_record: params
    });

  getAlayaCareData = async (
    applicationId: number
  ): Promise<{ res: { genders: string[]; states: string[]; titles: string[] } }> =>
    RecruitApiClient.get(`/api/v4/integrations/alayacare`, { application_id: applicationId });

  alayaCareExport = async (params: IAlayaCareExportParams): Promise<{ res: { success: string } }> =>
    RecruitApiClient.post(`/api/v4/integrations/alayacare`, params);

  updateFit2WorkStatus = async (applicationId: number, referenceId: number) =>
    RecruitApiClient.get(`/api/v4/integrations/fit2work/${referenceId}/update_status`, {
      application_id: applicationId
    });

  getCognologyData = async (applicationId: number): Promise<{ res: ICognologyData }> =>
    RecruitApiClient.get(`/api/v4/integrations/cognology_exports`, {
      application_id: applicationId
    });

  cognologyExport = async (
    params: ICognologyExportParams
  ): Promise<{ res: { success: string; approval_url?: string } }> =>
    RecruitApiClient.post(`/api/v4/integrations/cognology_exports`, {
      cognology_record: params
    });

  exportToCognology = async (applicationId: number): Promise<{ res: { success: string } }> =>
    RecruitApiClient.post(`/api/v4/integrations/cognology_exports/export`, {
      application_id: applicationId
    });

  getApprovalForms = async (): Promise<{ res: { requisitions: IApprovalForm[] } }> =>
    RecruitApiClient.get(`/api/v4/requisitions`, { override_limit: true, limit: 1000 });

  getApprovalForm = async (approvalFormId: number): Promise<{ res: IApprovalForm }> =>
    RecruitApiClient.get(`/api/v4/requisitions/${approvalFormId}`);

  getIntelliHRData = async (applicationId: number): Promise<{ res: IIntelliHrData }> =>
    RecruitApiClient.get(`/api/v4/integrations/intellihr`, { application_id: applicationId });

  exportToIntelliHR = async (applicationId: number, params: any) =>
    RecruitApiClient.post(`/api/v4/integrations/intellihr`, {
      application_id: applicationId,
      intellihr: params
    });

  updateAttachmentName = async (attachmentId: number, attachmentName: string) =>
    RecruitApiClient.put(`/admin/assets/${attachmentId}`, {
      asset: { attached_file_name: attachmentName }
    });

  getSourceOptions = async (
    sourceTypeId: number,
    countryId: number
  ): Promise<{ res: { sources: { id: number; name: string }[] } }> =>
    RecruitApiClient.get(`/api/v4/sources?source_type_id=${sourceTypeId}&country_id=${countryId}`);
}

export default new Api();
